import React, { useEffect, useRef, useState } from 'react';
import { API, authHeaders } from '../utils/helpers';

// -- The customer section of a staff booking form (18 Oct) --------------------
//
// Manny: the booking forms (drop-off, fit, rental) should show existing
// Lightspeed customers in a side panel, the way New Ticket does. Before this a
// phone booking only matched a returning customer if the phone or email typed
// matched Lightspeed exactly, so a customer with a new number or a typo became
// a second Lightspeed record.
//
// Same behaviour as NewTicketModal: search by name and by phone as staff type,
// click a match to use that customer, and editing any field afterwards lets go
// of the match (the details no longer describe the person picked).
//
// The parent owns the form. This reads/writes customerName, customerPhone and
// customerEmail on it, and reports the picked customer through onSelect. The
// picked id goes to the server as `lsCustomerId`, which the staff routes check
// exists in Lightspeed before using it (resolveOrCreateCustomer).

function fmtPhone(p) {
  if (!p) return '';
  const digits = String(p).replace(/\D/g, '').replace(/^1/, '');
  if (digits.length !== 10) return p;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const get = (q) => fetch(`${API}/api/customers?q=${encodeURIComponent(q)}`, { credentials: 'include', headers: authHeaders() })
  .then(r => (r.ok ? r.json() : [])).then(d => (Array.isArray(d) ? d : [])).catch(() => []);

export default function CustomerMatch({ T, isDark, input, form, setForm, selected, onSelect }) {
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  const name = form.customerName || '';
  const phone = form.customerPhone || '';

  useEffect(() => {
    if (selected) { setMatches([]); return undefined; }
    if (timer.current) clearTimeout(timer.current);
    const nameQ = name.trim();
    const digits = phone.replace(/\D/g, '');
    if (nameQ.length < 2 && digits.length < 4) { setMatches([]); setSearching(false); return undefined; }
    let live = true;
    timer.current = setTimeout(async () => {
      setSearching(true);
      const sets = await Promise.all([
        nameQ.length >= 2 ? get(nameQ) : [],
        digits.length >= 4 ? get(digits) : [],
      ]);
      if (!live) return;
      const seen = new Set();
      const merged = [];
      sets.flat().forEach(c => { if (c?.id && !seen.has(c.id)) { seen.add(c.id); merged.push(c); } });
      setMatches(merged.slice(0, 6));
      setSearching(false);
    }, 300);
    return () => { live = false; if (timer.current) clearTimeout(timer.current); };
  }, [name, phone, selected]);

  const edit = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    if (selected) onSelect(null);
  };
  const pick = (c) => {
    onSelect(c);
    setForm(f => ({
      ...f,
      customerName: c.name || [c.firstName, c.lastName].filter(Boolean).join(' '),
      customerPhone: c.phone || f.customerPhone,
      customerEmail: c.email || f.customerEmail,
    }));
  };

  const border = isDark ? '#2a3550' : '#d8d8d0';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 200px', gap: 12, marginBottom: 10 }}>
      <div>
        <div style={{ display: 'grid', gap: 8 }}>
          <input style={input} placeholder="Customer name *" value={name} onChange={edit('customerName')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input style={input} placeholder="Phone" value={phone} onChange={edit('customerPhone')} />
            <input style={input} placeholder="Email" value={form.customerEmail || ''} onChange={edit('customerEmail')} />
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, fontStyle: 'italic', color: selected ? '#2ecc71' : T.textSub, lineHeight: 1.45 }}>
          {selected
            ? <>✓ Using existing customer: {selected.name}{' '}
                <button type="button" onClick={() => onSelect(null)}
                  style={{ background: 'none', border: 'none', padding: 0, color: T.textSub, textDecoration: 'underline', cursor: 'pointer', fontSize: 12, fontStyle: 'normal' }}>
                  Change
                </button></>
            : 'Pick a match if this is a returning customer. Otherwise Spinboard matches by phone or email, and creates a new Lightspeed customer if neither is on file.'}
        </div>
      </div>

      <div style={{ background: isDark ? '#0f1724' : '#fff', border: `1px solid ${border}`, borderRadius: 8, padding: '8px 10px', alignSelf: 'start', minHeight: 104 }}>
        <div style={{ fontSize: 10, color: isDark ? '#5a6a8a' : '#999', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 6 }}>Possible matches</div>
        {searching && <div style={{ fontSize: 12, color: T.textSub, fontStyle: 'italic' }}>Searching…</div>}
        {!searching && matches.length === 0 && (
          <div style={{ fontSize: 12, color: T.textSub, fontStyle: 'italic' }}>
            {selected ? 'Customer selected' : 'Type a name or phone number to search Lightspeed'}
          </div>
        )}
        {!searching && matches.map(c => (
          <div key={c.id} role="button" tabIndex={0} onClick={() => pick(c)}
            onKeyDown={e => { if (e.key === 'Enter') pick(c); }}
            style={{ padding: '6px 7px', borderRadius: 6, cursor: 'pointer', marginBottom: 3, border: '1px solid transparent' }}
            onMouseEnter={e => { e.currentTarget.style.background = isDark ? '#1a2540' : '#f4f4ef'; e.currentTarget.style.borderColor = '#e67e22'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#60a5fa' : '#3498db' }}>{c.name}</div>
            <div style={{ fontSize: 11, color: T.textSub }}>{fmtPhone(c.phone) || c.email || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
